import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { type Company, companyListQueryOptions } from '@/entities/company'
import { useMyProjectIds } from '@/entities/employee'
import { projectListQueryOptions } from '@/entities/project'
import { ArchiveCompanyDialog } from '@/features/archive-company'
import { CreateCompanyDialog } from '@/features/create-company'
import { CompanyName } from '@/features/rename-company'
import { useToggleCompanyVisibility } from '@/features/toggle-visibility'
import { cn, pluralRu } from '@/shared/lib'
import { rememberListFilters, useStickyListFilters } from '@/shared/model'
import { Button } from '@/shared/ui/button'
import { Card } from '@/shared/ui/card'
import { Checkbox } from '@/shared/ui/checkbox'
import { EmptyState } from '@/shared/ui/empty-state'
import { Icon } from '@/shared/ui/icon'
import { Menu, MenuItem } from '@/shared/ui/menu'
import { PageHeader } from '@/shared/ui/page-header'

interface CompaniesSearch {
  archived?: boolean
  hidden?: boolean
  mine?: boolean
}

export const Route = createFileRoute('/companies')({
  component: CompaniesPage,
  head: () => ({ meta: [{ title: 'Компании · STIT Task' }] }),
  // Тумблеры «показать архивные»/«показать скрытые»/«только мои» живут в URL
  // (шарятся, переживают перезагрузку). Дефолт (false) в схему не пишем — URL чистый.
  validateSearch: (s: Record<string, unknown>): CompaniesSearch => ({
    ...(s.archived === true || s.archived === 'true' ? { archived: true } : {}),
    ...(s.hidden === true || s.hidden === 'true' ? { hidden: true } : {}),
    ...(s.mine === true || s.mine === 'true' ? { mine: true } : {}),
  }),
})

const SKELETON_ROWS = ['s1', 's2', 's3']

function CompaniesSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      {SKELETON_ROWS.map((s) => (
        <div key={s} className="h-16 animate-pulse rounded-card bg-bg-elevated" />
      ))}
    </div>
  )
}

// Строка компании: имя-ссылка на страницу компании (drill-down), inline-rename
// карандашом, счётчик видимых проектов, «…»-меню архивации. Архивная — приглушена,
// read-only (только переход).
function CompanyRow({
  company,
  projectCount,
  hidden,
}: {
  company: Company
  projectCount: number
  hidden: boolean
}) {
  const [archiveOpen, setArchiveOpen] = useState(false)
  const toggleVisibility = useToggleCompanyVisibility(company.id)
  const editable = !company.archived

  return (
    <Card className={cn('flex items-center gap-3', (company.archived || hidden) && 'opacity-60')}>
      <Icon name="building" size={18} className="shrink-0 text-text-tertiary" />
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <CompanyName
          company={company}
          editable={editable}
          display={
            <Link
              to="/companies/$companyId"
              params={{ companyId: company.id }}
              className="t-h3 min-w-0 truncate text-text-primary transition-colors hover:text-accent"
            >
              {company.name}
            </Link>
          }
        />
        {company.archived && <span className="label shrink-0 text-text-tertiary">в архиве</span>}
        {hidden && <span className="label shrink-0 text-text-tertiary">скрыто</span>}
      </div>
      <span className="shrink-0 text-sm text-text-tertiary">
        {projectCount} {pluralRu(projectCount, ['проект', 'проекта', 'проектов'])}
      </span>
      {editable ? (
        <Menu
          align="end"
          ariaLabel={`Действия с компанией «${company.name}»`}
          triggerClassName="text-text-tertiary hover:text-text-primary"
          trigger={<Icon name="more" size={16} />}
        >
          {(close) => (
            <>
              <MenuItem
                onSelect={() => {
                  toggleVisibility.mutate(!hidden)
                  close()
                }}
              >
                <span className="inline-flex items-center gap-2">
                  <Icon name={hidden ? 'eye' : 'eye-off'} size={14} />
                  {hidden ? 'Показать в task' : 'Скрыть в task'}
                </span>
              </MenuItem>
              <MenuItem
                className="text-accent hover:text-accent"
                onSelect={() => {
                  setArchiveOpen(true)
                  close()
                }}
              >
                <span className="inline-flex items-center gap-2">
                  <Icon name="archive" size={14} />
                  Архивировать
                </span>
              </MenuItem>
            </>
          )}
        </Menu>
      ) : (
        <Icon name="chevron-right" size={16} className="shrink-0 text-text-tertiary" />
      )}
      {archiveOpen && (
        <ArchiveCompanyDialog
          companyId={company.id}
          companyName={company.name}
          onClose={() => setArchiveOpen(false)}
        />
      )}
    </Card>
  )
}

// short — подпись для узкого экрана: полные («Показывать архивные») втроём занимают
// на телефоне три строки шапки.
function FilterToggle({
  label,
  short,
  checked,
  onChange,
}: {
  label: string
  short: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <Checkbox
      // Доступное имя всегда полное — видимый текст лишь укорачивается на узком
      // экране, поэтому он aria-hidden, а имя задаёт aria-label.
      aria-label={label}
      label={
        <span aria-hidden>
          <span className="sm:hidden">{short}</span>
          <span className="hidden sm:inline">{label}</span>
        </span>
      }
      labelClassName="text-sm text-text-secondary"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
    />
  )
}

function CompaniesPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const {
    archived: includeArchived,
    hidden: includeHidden,
    mine: onlyMine,
  } = useStickyListFilters(search)
  // CompanyView не несёт hidden — берём два серверных набора (полный и видимый),
  // hidden выводим их разницей. archived=true всегда: держим архивные в кэше,
  // тумблер «Показывать архивные» фильтрует их клиентом. Оба запроса дедуплятся.
  const companiesAllQ = useQuery(companyListQueryOptions(true, true))
  const companiesVisibleQ = useQuery(companyListQueryOptions(true, false))
  // Список проектов уже нужен приложению (лёгкий) — отсюда счётчики на строках.
  const projectsQ = useQuery(projectListQueryOptions(includeArchived))
  const myProjects = useMyProjectIds(onlyMine)
  const [createOpen, setCreateOpen] = useState(false)

  // Тумблеры живут в URL совместно — патч поверх текущего набора, чтобы они не
  // затирали друг друга. Тот же набор уходит в память списков: страница компании
  // подхватит выбор, сделанный здесь.
  const applySearch = (patch: Partial<CompaniesSearch>) => {
    const next = { archived: includeArchived, hidden: includeHidden, mine: onlyMine, ...patch }
    rememberListFilters(next)
    void navigate({
      search: {
        ...(next.archived ? { archived: true } : {}),
        ...(next.hidden ? { hidden: true } : {}),
        ...(next.mine ? { mine: true } : {}),
      },
    })
  }

  const header = (
    <PageHeader
      title="Компании"
      eyebrow="Обзор"
      actions={
        <>
          <FilterToggle
            label="Только мои"
            short="Мои"
            checked={onlyMine}
            onChange={(v) => applySearch({ mine: v })}
          />
          <FilterToggle
            label="Показывать архивные"
            short="Архивные"
            checked={includeArchived}
            onChange={(v) => applySearch({ archived: v })}
          />
          <FilterToggle
            label="Показывать скрытые"
            short="Скрытые"
            checked={includeHidden}
            onChange={(v) => applySearch({ hidden: v })}
          />
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Icon name="plus" size={14} />
            Компания
          </Button>
        </>
      }
    />
  )

  const isPending =
    companiesAllQ.isPending ||
    companiesVisibleQ.isPending ||
    projectsQ.isPending ||
    myProjects.isPending
  const isError = companiesAllQ.isError || companiesVisibleQ.isError || projectsQ.isError

  // Под фильтром «Только мои» счётчик строки считает мои проекты — он же решает,
  // остаётся ли компания в списке: своего членства в компании нет, участие
  // выводится из проектов.
  const allProjects = projectsQ.data ?? []
  const projects = myProjects.ids
    ? allProjects.filter((p) => myProjects.ids?.has(p.id))
    : allProjects
  const countFor = (companyId: string) => projects.filter((p) => p.companyId === companyId).length

  const allCompanies = companiesAllQ.data ?? []
  const visibleIds = new Set((companiesVisibleQ.data ?? []).map((c) => c.id))
  const isHidden = (id: string) => !visibleIds.has(id)
  // Базовый набор: со скрытыми (тумблер) или только видимые. Поверх — архивный
  // фильтр: архивные компании скрыты из общего списка, пока не включён тумблер.
  const base = includeHidden ? allCompanies : allCompanies.filter((c) => !isHidden(c.id))
  const byArchived = base.filter((c) => !c.archived || includeArchived)
  const visibleCompanies = myProjects.ids
    ? byArchived.filter((c) => countFor(c.id) > 0)
    : byArchived

  return (
    <div className="container-x px-4 py-8 sm:px-6 sm:py-10">
      {header}
      {isPending ? (
        <CompaniesSkeleton />
      ) : isError ? (
        <div className="flex flex-col items-center gap-4 rounded-card bg-bg-surface py-14 text-center">
          <p className="text-text-secondary">Не удалось загрузить компании</p>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              if (companiesAllQ.isError) void companiesAllQ.refetch()
              if (companiesVisibleQ.isError) void companiesVisibleQ.refetch()
              if (projectsQ.isError) void projectsQ.refetch()
            }}
          >
            Повторить
          </Button>
        </div>
      ) : visibleCompanies.length === 0 ? (
        onlyMine ? (
          <EmptyState
            icon={<Icon name="building" size={40} />}
            title="Вы не участвуете ни в одном проекте"
            description="Фильтр показывает компании, где у вас есть проект. Участников проекта назначают на странице компании."
            action={
              <Button variant="secondary" onClick={() => applySearch({ mine: false })}>
                Показать все компании
              </Button>
            }
          />
        ) : (
          <EmptyState
            icon={<Icon name="building" size={40} />}
            title="Создайте первую компанию"
            description="Компания — клиент студии: у компании проекты, у проектов — доски отделов. Заведите компанию, затем провалитесь в неё и добавьте проект."
            action={
              <Button onClick={() => setCreateOpen(true)}>
                <Icon name="plus" size={14} />
                Компания
              </Button>
            }
          />
        )
      ) : (
        <div className="flex flex-col gap-3">
          {visibleCompanies.map((c) => (
            <CompanyRow
              key={c.id}
              company={c}
              projectCount={countFor(c.id)}
              hidden={isHidden(c.id)}
            />
          ))}
        </div>
      )}
      {createOpen && <CreateCompanyDialog onClose={() => setCreateOpen(false)} />}
    </div>
  )
}
